import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api } from "../api";
import type { SaveSettlementAdjustmentInput, SettlementRecord, SettlementSource, Worker } from "../types";
import { readAmount, today } from "../payrollPresentation";
import { PayrollAmountInput } from "./PayrollAmountInput";
import { Select } from "./Select";
import { useAcknowledgedMutation } from "./useAcknowledgedMutation";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { Feedback } from "./ui/Surface";
import { Modal } from "./ui/Modal";
import { ChevronLeft } from "lucide-react";
import { settlementWorkerLabel, useSettlementWorkers } from "./useSettlementWorkers";

/* memo 칸은 프로젝트명을 싣는다. 서버가 이 값을 표의 "프로젝트" 열로 옮기고, "작업" 열에는
   공제 / 추가 지급이 그대로 찍힌다. 이름을 바꾸지 않은 것은 memo가 이 앱의 다른 곳에서도
   쓰는 말이라, 여기만 고치면 오히려 같은 말이 두 뜻을 갖기 때문이다. */
type Draft = { workerId: string; date: string; kind: "deduction" | "addition"; amount: string; memo: string };

const validate = (draft: Draft, workers: Pick<Worker, "id">[]) => {
  const amount = readAmount(draft.amount);
  return {
    workerId: workers.some(worker => worker.id === draft.workerId) ? "" : "작업자를 선택해 주세요.",
    date: /^\d{4}-\d{2}-\d{2}$/.test(draft.date) ? "" : "정산일을 확인해 주세요.",
    amount: amount !== null && amount > 0 && amount <= 1_000_000_000 ? "" : "1원 이상 10억원 이하의 금액을 입력해 주세요.",
    memo: draft.memo.trim() && draft.memo.trim().length <= 100 ? "" : "사유를 1~100자로 입력해 주세요.",
  };
};

/**
 * 작업에서 나오지 않은 돈을 다는 곳. 표의 금액은 수량 × 단가로 계산된 값이라 직접 고치면
 * 불출·회수 기록과 어긋난다. 그래서 원본은 두고 별도 줄을 더한다 — 고치는 화면이 아니라
 * 더하는 화면이므로 이름도 표에 찍히는 말(공제 / 추가 지급) 그대로 쓴다.
 */
export function PayrollAdjustmentEditor({ source, record, prefill, kind, kindPicker, workers, actorName, refresh, onSaved, onClose, restoreFocus }: {
  source: SettlementSource;
  record?: SettlementRecord;
  /* 밖에서 갈래를 쥐면(수동 정산의 구분 고르개) 창 안의 공제·추가 칸은 접는다 — 같은 것을
     두 곳에서 고르면 어느 쪽이 진짜인지 매번 정해야 한다. */
  kind?: "deduction" | "addition";
  /** 오면 이 폼은 "정산 등록" 팝업의 한 갈래로 선다. */
  kindPicker?: ReactNode;
  prefill?: { workerId: string; date: string };
  workers: Worker[];
  actorName: string;
  refresh: () => Promise<void>;
  onSaved: () => void;
  onClose: () => void;
  restoreFocus: () => HTMLElement | null;
}) {
  const [initial] = useState<Draft>(() => ({
    workerId: record?.workerId ?? prefill?.workerId ?? "",
    date: record?.settlementDate ?? prefill?.date ?? today(),
    kind: record?.adjustmentKind ?? "deduction",
    amount: String(record?.adjustmentAmount || ""),
    memo: record?.title === "공제" || record?.title === "추가 지급" ? "" : record?.title ?? "",
  }));
  const [draft, setDraft] = useState(initial), [attempts, setAttempts] = useState(0);
  useEffect(() => { if (kind) setDraft(current => current.kind === kind ? current : { ...current, kind }); }, [kind]);
  const content = useRef<HTMLDivElement>(null);
  const mutation = useAcknowledgedMutation(refresh, onSaved);
  const blocked = mutation.busy || mutation.committed;
  const dirty = !mutation.committed && JSON.stringify(draft) !== JSON.stringify(initial);
  /* 닫을 때 되묻지 않는다. 칸 몇 개짜리 창이라 다시 여는 비용이 확인 창을 읽는 비용보다
     작고, 이 화면의 다른 동작들도 확인 없이 바로 처리한다. 저장 중일 때만 막는다. */
  const canClose = () => !mutation.busy;
  const workerOptions = useSettlementWorkers(workers);
  const { people } = workerOptions;
  const amount = readAmount(draft.amount);
  const allErrors = validate(draft, people);
  const errors: Partial<Record<keyof typeof allErrors, string>> = attempts ? allErrors : {};
  const hasErrors = Object.values(errors).some(Boolean);
  useLayoutEffect(() => {
    if (!hasErrors) return;
    content.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus({ preventScroll: true });
  }, [attempts]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    setAttempts(value => value + 1);
    if (Object.values(allErrors).some(Boolean) || !amount) return;
    const input: SaveSettlementAdjustmentInput = {
      source, workerId: draft.workerId, settlementDate: draft.date, kind: draft.kind,
      amount, memo: draft.memo, createdBy: actorName,
    };
    void mutation.execute(() => record ? api.updateSettlementAdjustment(record.id, input) : api.createSettlementAdjustment(input));
  };
  /* 갈래를 바꿔도 제목은 그대로다 — 같은 창에서 구분만 바꾼 것이라, 제목이 사라지거나
     바뀌면 창이 갈아 끼워진 것처럼 보인다. */
  return <Modal title={record ? "공제·추가 지급 수정" : kindPicker ? "수동 정산" : "공제·추가 지급 등록"} className="payroll-entry-dialog" busy={mutation.busy}
    /* 정산 기록 창 위에 한 걸음 얹힌 창이다 — ‹ 로 표로 돌아가고, ✕ 는 정산까지 통째로
       떠난다. 두 걸음짜리 창은 어디서든 같은 문법을 쓴다. */
    onBack={() => { if (canClose()) onClose(); }} onClose={() => { if (canClose()) onClose(); }} restoreFocus={restoreFocus}>
    <form className="payroll-entry-form" noValidate onSubmit={submit} data-testid="settlement-adjustment-form">
      <div ref={content} className="payroll-entry-content">
        <div className="worker-profile-page-title">
          <button type="button" className="worker-profile-page-back" aria-label="뒤로" disabled={mutation.busy} onClick={() => { if (canClose()) onClose(); }}><ChevronLeft size={20} aria-hidden="true" /></button>
          <span aria-hidden="true">{record ? "공제·추가 지급 수정" : kindPicker ? "수동 정산" : "공제·추가 지급 등록"}</span>
        </div>
        {mutation.error && <Feedback tone={mutation.committed ? "info" : "error"}>{mutation.error}{mutation.committed && <Button size="compact" onClick={() => void mutation.retry()}>상태 다시 확인</Button>}</Feedback>}
        {workerOptions.error && <Feedback tone="error">{workerOptions.error}<Button size="compact" onClick={workerOptions.retry}>다시 불러오기</Button></Feedback>}
        {/* 다른 갈래와 같은 격자·같은 말. 소제목은 칸 이름을 한 번 더 말할 뿐이라 뺀다. */}
        <div className="payroll-form-grid">
          {/* 누구인지를 먼저 고른다 — PayrollEntryEditor와 같은 순서다. */}
          {/* 두 줄로 읽는다: 첫 줄은 "누구에게 무엇을 얼마", 둘째 줄은 "언제 왜". 칸이 여섯이라
              한 줄로 늘어놓으면 눈이 어디서 끊어 읽어야 할지 모른다. */}
          <Field label="작업자" required error={errors.workerId}><Select required disabled={blocked} value={draft.workerId} onChange={event => setDraft(value => ({ ...value, workerId: event.target.value }))} data-testid="adjustment-worker"><option value="">작업자 선택</option>{people.map(worker => <option key={worker.id} value={worker.id}>{settlementWorkerLabel(worker)}</option>)}</Select></Field>
          {kindPicker}
          {!kind && <Field label="공제·추가" required><Select disabled={blocked} value={draft.kind} onChange={event => setDraft(value => ({ ...value, kind: event.target.value as Draft["kind"] }))} data-testid="adjustment-kind"><option value="deduction">공제</option><option value="addition">추가 지급</option></Select></Field>}
          <Field label="정산 금액" required error={errors.amount}><PayrollAmountInput required disabled={blocked} sign={draft.kind === "deduction" ? "minus" : "plus"} value={draft.amount} onValue={value => setDraft(current => ({ ...current, amount: value }))} testId="adjustment-amount" label="정산 금액" /></Field>
          <Field label="정산일" required error={errors.date}><Input required disabled={blocked} type="date" value={draft.date} onChange={event => setDraft(value => ({ ...value, date: event.target.value }))} data-testid="adjustment-date" /></Field>
          <Field label="사유" required error={errors.memo}><Input required disabled={blocked} value={draft.memo} maxLength={100} onChange={event => setDraft(value => ({ ...value, memo: event.target.value }))} data-testid="adjustment-project" /></Field>
          {/* "세후 영향액"은 뺐다. 공제일 때는 적어 넣은 금액에 빼기 부호만 붙여 되풀이했고,
              추가일 때는 이 한 줄만 따로 3.3%를 떼어 보여 줬는데 — 원천징수는 그 사람의 그
              달 합계에 한 번 매기는 것이라, 여기 적힌 수는 실제로 들어갈 돈이 아니다.
              여기서 정하는 것은 "얼마를 더하거나 뺄까"이고, 세금은 묶을 때 셈한다. */}
        </div>
      </div>
      <footer className="payroll-editor-footer"><span role="status">{mutation.phase === "writing" ? "등록 중…" : mutation.phase === "refreshing" ? "목록 확인 중…" : ""}</span><Button variant="primary" type="submit" disabled={blocked}>{record ? "변경 저장" : "등록"}</Button></footer>
    </form>
  </Modal>;
}
